import React, { useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { mdiPlus } from "@mdi/js";
import MdiIcon from "../../components/device/app/MdiIcon";
import Main from "../../components/layout/Main";
import { DEVICE_TYPES, isType } from "../../constants/devices";
import { summaryText, TYPE_LABELS } from "../../automation/format";
import { lookOf } from "../../automation/look";
import { useAutomationFeature } from "../../automation/AutomationProvider";
import {
  useAutomations,
  useCanManageAutomations,
  useConfirm,
  useRunAutomation,
  useToggleAutomation,
} from "../../automation/useAutomations";
import { settingsPathFor } from "../Device/settings/paths";
import AddAutomationSheet from "./AddAutomationSheet";
import { AutomationSwitch, RunButton } from "./AutomationCard";
import { AUTOMATION_PATHS } from "./paths";
import "../../assets/styles/app-automation.css";

// Everything that uses a device, in one place
// (app/screens/Automation/DeviceAutomationsPanel.js): on the device page's
// panel, next to its usage history, or full page for devices without one.
// What the device does by itself (its own inching or cycling) opens its
// settings. Owners add automations for it from Add.
export const DeviceAutomationsPanel = ({ device }) => {
  const navigate = useHistory();
  const location = useLocation();
  const { lookup } = useAutomationFeature();
  const canManage = useCanManageAutomations();
  const { items, loading, error, reload, replace } = useAutomations({ applianceId: device?.applianceId });
  const { confirm, dialog } = useConfirm();
  const run = useRunAutomation(confirm, reload);
  const toggle = useToggleAutomation(replace);
  const [adding, setAdding] = useState(false);
  const list = items ?? [];
  const settingsPath = settingsPathFor(device);

  // its own settings, where on-device cycling is changed: the water level
  // controller's pump is Motor Settings' motor 2, as the app opens it
  const openSettings = () => {
    const motor2 = isType(device, DEVICE_TYPES.WATER_LEVEL_CONTROLLER) ? { index: 1 } : {};
    if (settingsPath) navigate.push(settingsPath, { data: device, ...motor2 });
  };

  // Back from an automation, or from adding one, comes back to this list: the
  // device page opens the tab its history entry names.
  const keepTab = () => navigate.replace(location.pathname, { ...location.state, panel: "automations" });

  const open = (item) => {
    if (item.readOnly) {
      openSettings();
      return;
    }
    keepTab();
    navigate.push(AUTOMATION_PATHS.DETAIL, { id: item.id, automation: item });
  };

  return (
    <div className="hx-auto-device">
      {canManage ? (
        <div className="hx-auto-device-top">
          <button
            type="button"
            className="hx-auto-new"
            onClick={() => setAdding(true)}
            aria-label={`Add an automation to ${device?.name || "this device"}`}
          >
            <MdiIcon path={mdiPlus} size={18} />
            Add
          </button>
        </div>
      ) : null}
      {list.length ? (
        <ul className="hx-auto-device-list">
          {list.map((item) => {
            const look = lookOf(item);
            const can = item.can || {};
            return (
              <li key={item.id} className="hx-auto-device-row">
                <span className="hx-auto-device-icon" style={{ "--hx-auto-type": look.color }} aria-hidden="true">
                  <MdiIcon path={look.icon} size={18} />
                </span>
                <span className="hx-auto-device-text">
                  <button type="button" className="hx-auto-open" onClick={() => open(item)} disabled={item.readOnly && !settingsPath}>
                    {item.name}
                  </button>
                  <span>
                    {/* tap-to-run and NFC summaries already start with their kind */}
                    {[["tap", "nfc"].includes(item.type) ? null : TYPE_LABELS[item.type], summaryText(item, lookup)].filter(Boolean).join(" · ")}
                  </span>
                </span>
                {item.type === "tap" && can.run ? (
                  <RunButton small onRun={() => run(item)} label={`Run ${item.name}`} />
                ) : can.edit && !item.readOnly ? (
                  <AutomationSwitch automation={item} onToggle={(enabled) => toggle(item, enabled)} />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {items === null || (loading && !list.length) ? (
        <div className="hx-skel hx-auto-skel" />
      ) : !list.length ? (
        <p className="hx-auto-device-empty" role={error ? "alert" : undefined}>
          {error ||
            (canManage ? "Nothing uses this device yet. Use Add to start." : "No automations use this device.")}
        </p>
      ) : (
        <p className="hx-auto-device-foot">Everything that uses {device?.name || "this device"}, in one place.</p>
      )}
      <AddAutomationSheet
        open={adding}
        device={device}
        onClose={() => setAdding(false)}
        onPick={(type, { event } = {}) => {
          setAdding(false);
          keepTab();
          navigate.push(AUTOMATION_PATHS.CREATE, { type, applianceId: device.applianceId, event });
        }}
        onDeviceSettings={() => {
          setAdding(false);
          openSettings();
        }}
      />
      {dialog}
    </div>
  );
};

// The same list full page, for devices whose page has no history panel.
function DeviceAutomations() {
  const { state } = useLocation();
  const device = state?.data;
  if (!device) return <Redirect to="/dashboard" />;
  return (
    <Main title="Automations">
      <div className="hx-auto-detail">
        <p className="hx-set-caption">{device.name}</p>
        <section className="hx-auto-box">
          <DeviceAutomationsPanel device={device} />
        </section>
      </div>
    </Main>
  );
}

export default DeviceAutomations;
